import { useLocation } from 'react-router-dom';
import { asRows } from './rows';
import { asRpcError, useLiveQuery, type LiveState } from './rpc';

/**
 * audit-b-receivables (C5, 0307): the loads waiting to be invoiced to their broker (POD received or legacy
 * invoiced, or canceled with a TONU, and no receivable yet), oldest first as the server orders them, with the
 * instant each started waiting. `receivables_pending_read` is SECURITY INVOKER: RLS limits it to the carriers the
 * caller sees. Read again on every navigation (the sidebar count follows the work as it is done).
 */

export interface PendingInvoice {
  loadId: string;
  /** The latest pod_received (or canceled, for a TONU) event; null for imported data without one. */
  sinceAt: string | null;
  reason: 'pod_received' | 'tonu';
}

export function useReceivablesPending(): LiveState<PendingInvoice[]> {
  const { pathname } = useLocation();
  return useLiveQuery<PendingInvoice[]>(
    async (client, agencyId) => {
      const { data, error } = await client.rpc('receivables_pending_read', { p_agency: agencyId });
      if (error) return { error: asRpcError(error)! };
      return {
        data: asRows(data).map((r) => ({
          loadId: String(r.load_id),
          sinceAt: typeof r.since_at === 'string' ? r.since_at : null,
          reason: r.reason === 'tonu' ? ('tonu' as const) : ('pod_received' as const),
        })),
      };
    },
    [pathname],
  );
}
